import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import { TextStyle, FontSize } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import { Highlight } from '@tiptap/extension-highlight'
import PropTypes from 'prop-types'
import { FiPhone, FiLink } from 'react-icons/fi'
import sanitizeHtml from 'sanitize-html'
import './iar-rte.scss'

const useOnClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) return
      handler(event)
    }
    document.addEventListener('mousedown', listener)
    document.addEventListener('touchstart', listener)
    return () => {
      document.removeEventListener('mousedown', listener)
      document.removeEventListener('touchstart', listener)
    }
  }, [ref, handler])
}

const PhoneButton = ({ editor, disableHandleChange }) => {
  const [phoneData, setPhoneData] = useState({ title: '', number: '' })
  const [modal, showModal] = useState(false)
  const [isDisabled, setIsDisabled] = useState(true)
  const divRef = useRef()

  const handleInputChange = (event) => {
    setPhoneData({ ...phoneData, [event.target.name]: event.target.value })
  }

  useEffect(() => {
    setIsDisabled(phoneData.title === '' || phoneData.number === '')
  }, [phoneData])

  const handler = useCallback(() => showModal(false), [])
  useOnClickOutside(divRef, handler)

  const addPhone = () => {
    if (editor) {
      editor
        .chain()
        .focus()
        .insertContent(`<a target="_self" href="tel:${phoneData.number}">${phoneData.title}</a>`)
        .run()
    }
    showModal(false)
    setPhoneData({ title: '', number: '' })
  }

  return (
    <div className="phone-wrapper">
      <div
        className="phone-tool-button"
        onClick={() => {
          disableHandleChange ? showModal(false) : showModal(true)
        }}
      >
        <FiPhone size={16} />
      </div>
      <div className={`phone-modal ${modal ? 'visible' : 'not-visible'}`} ref={divRef}>
        <label className="phone-label" htmlFor="title">
          Link Title
        </label>
        <input
          className="phone-input"
          type="text"
          name="title"
          value={phoneData.title}
          onChange={handleInputChange}
        />
        <label className="phone-label" htmlFor="number">
          Link Phone
        </label>
        <input
          className="phone-input"
          type="tel"
          pattern="[\+]\d{2}[\(]\d{2}[\)]\d{4}[\-]\d{4}"
          name="number"
          value={phoneData.number}
          onChange={handleInputChange}
        />
        <div className="phone-buttons-container">
          <button
            type="button"
            disabled={isDisabled}
            className={`phone-button ${isDisabled ? 'disabled' : ''}`}
            onClick={addPhone}
          >
            Add Phone
          </button>
          <label className="phone-button" onClick={() => showModal(false)}>
            Cancel
          </label>
        </div>
      </div>
    </div>
  )
}

const LinkButton = ({ editor }) => {
  const [showInput, setShowInput] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const wrapperRef = useRef()

  const handler = useCallback(() => setShowInput(false), [])
  useOnClickOutside(wrapperRef, handler)

  const applyLink = () => {
    if (linkUrl && editor) {
      editor.chain().focus().setLink({ href: linkUrl }).run()
    }
    setShowInput(false)
    setLinkUrl('')
  }

  return (
    <div className="rdw-link-wrapper" ref={wrapperRef}>
      <div
        className={`rdw-option-wrapper${editor && editor.isActive('link') ? ' rdw-option-active' : ''}`}
        onMouseDown={(e) => {
          e.preventDefault()
          if (editor && editor.isActive('link')) {
            editor.chain().focus().unsetLink().run()
          } else {
            setShowInput((prev) => !prev)
          }
        }}
        title="Link"
      >
        <FiLink size={14} />
      </div>
      {showInput && (
        <div className="rdw-link-modal">
          <input
            type="url"
            className="rdw-link-modal-input"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://..."
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                applyLink()
              }
            }}
            autoFocus
          />
          <div className="rdw-link-modal-buttonsection">
            <button
              type="button"
              className="rdw-link-modal-btn"
              onMouseDown={(e) => {
                e.preventDefault()
                applyLink()
              }}
              disabled={!linkUrl}
            >
              Add
            </button>
            <button
              type="button"
              className="rdw-link-modal-btn"
              onMouseDown={(e) => {
                e.preventDefault()
                setShowInput(false)
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const COLOR_PALETTE = [
  '#1e6b35',
  '#0e7490',
  '#7dd3fc',
  '#1d4ed8',
  '#7c3aed',
  '#374151',
  '#22c55e',
  '#06b6d4',
  '#93c5fd',
  '#3b82f6',
  '#a78bfa',
  '#9ca3af',
  '#1e3a5f',
  '#111827',
  '#ca8a04',
  '#c2410c',
  '#b91c1c',
  '#d97706',
  '#d1d5db',
  '#f9fafb',
  '#fef08a',
  '#fed7aa',
  '#fecaca',
  '#ffedd5',
  '#dc2626',
  '#e5e7eb'
]

const ColorPicker = ({ editor }) => {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState('text')
  const wrapperRef = useRef()

  const handler = useCallback(() => setOpen(false), [])
  useOnClickOutside(wrapperRef, handler)

  const currentTextColor = editor.getAttributes('textStyle').color || '#111827'
  const currentHighlight = editor.getAttributes('highlight').color || 'transparent'

  const applyColor = (color) => {
    if (tab === 'text') {
      editor.chain().focus().setColor(color).run()
    } else {
      editor.chain().focus().setHighlight({ color }).run()
    }
  }

  const activeColor = tab === 'text' ? currentTextColor : currentHighlight

  return (
    <div className="rte-colorpicker-wrapper" ref={wrapperRef}>
      <div
        className="rdw-option-wrapper rte-colorpicker-btn"
        onMouseDown={(e) => {
          e.preventDefault()
          setOpen((prev) => !prev)
        }}
        title="Text Color / Highlight"
      >
        <span
          className="rte-colorpicker-indicator"
          style={{ borderBottomColor: tab === 'text' ? currentTextColor : currentHighlight }}
        >
          A
        </span>
      </div>
      {open && (
        <div className="rte-colorpicker-dropdown">
          <div className="rte-colorpicker-tabs">
            <button
              type="button"
              className={`rte-colorpicker-tab${tab === 'text' ? ' active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault()
                setTab('text')
              }}
            >
              Text
            </button>
            <button
              type="button"
              className={`rte-colorpicker-tab${tab === 'highlight' ? ' active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault()
                setTab('highlight')
              }}
            >
              Highlight
            </button>
          </div>
          <div className="rte-colorpicker-grid">
            {COLOR_PALETTE.map((color) => (
              <div
                key={color}
                className={`rte-colorpicker-swatch${activeColor === color ? ' active' : ''}`}
                style={{ backgroundColor: color }}
                onMouseDown={(e) => {
                  e.preventDefault()
                  applyColor(color)
                }}
                title={color}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const FONT_SIZES = [
  '8',
  '10',
  '12',
  '14',
  '16',
  '18',
  '20',
  '24',
  '28',
  '32',
  '36',
  '48',
  '72',
  '96'
]

const FontSizeSelect = ({ editor }) => {
  const currentSize = editor.getAttributes('textStyle').fontSize
    ? editor.getAttributes('textStyle').fontSize.replace('px', '')
    : '16'

  const handleChange = (e) => {
    const val = e.target.value
    if (!val) {
      editor.chain().focus().unsetFontSize().run()
    } else {
      editor.chain().focus().setFontSize(`${val}px`).run()
    }
  }

  return (
    <div className="rdw-blocktype-wrapper">
      <select
        className="rdw-blocktype-select"
        value={currentSize}
        onChange={handleChange}
        title="Font Size"
        style={{ width: '52px' }}
      >
        {FONT_SIZES.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>
    </div>
  )
}

const BlockTypeSelect = ({ editor }) => {
  const getActiveBlockType = () => {
    if (editor.isActive('codeBlock')) return 'code'
    if (editor.isActive('blockquote')) return 'blockquote'
    if (editor.isActive('heading', { level: 1 })) return '1'
    if (editor.isActive('heading', { level: 2 })) return '2'
    if (editor.isActive('heading', { level: 3 })) return '3'
    if (editor.isActive('heading', { level: 4 })) return '4'
    if (editor.isActive('heading', { level: 5 })) return '5'
    if (editor.isActive('heading', { level: 6 })) return '6'
    return 'normal'
  }

  const handleChange = (e) => {
    const val = e.target.value
    const chain = editor.chain().focus().clearNodes()
    switch (val) {
      case 'normal':
        chain.run()
        break
      case 'blockquote':
        chain.toggleBlockquote().run()
        break
      case 'code':
        chain.toggleCodeBlock().run()
        break
      default:
        chain.toggleHeading({ level: parseInt(val) }).run()
    }
  }

  return (
    <div className="rdw-blocktype-wrapper">
      <select
        className="rdw-blocktype-select"
        value={getActiveBlockType()}
        onChange={handleChange}
        title="Block Type"
      >
        <option value="normal">Normal</option>
        <option value="1">H1</option>
        <option value="2">H2</option>
        <option value="3">H3</option>
        <option value="4">H4</option>
        <option value="5">H5</option>
        <option value="6">H6</option>
        <option value="blockquote">Blockquote</option>
        <option value="code">Code</option>
      </select>
    </div>
  )
}

const Toolbar = ({ editor, disableHandleChange }) => {
  if (!editor) return null

  return (
    <div className={`rdw-editor-toolbar${disableHandleChange ? ' rdw-toolbar-disabled' : ''}`}>
      <div className="rdw-inline-wrapper">
        <div
          className={`rdw-option-wrapper${editor.isActive('bold') ? ' rdw-option-active' : ''}`}
          onMouseDown={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleBold().run()
          }}
          title="Bold"
        >
          <b>B</b>
        </div>
        <div
          className={`rdw-option-wrapper${editor.isActive('italic') ? ' rdw-option-active' : ''}`}
          onMouseDown={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleItalic().run()
          }}
          title="Italic"
        >
          <i>i</i>
        </div>
        <div
          className={`rdw-option-wrapper${editor.isActive('underline') ? ' rdw-option-active' : ''}`}
          onMouseDown={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleUnderline().run()
          }}
          title="Underline"
        >
          <u>U</u>
        </div>
      </div>
      <BlockTypeSelect editor={editor} />
      <FontSizeSelect editor={editor} />
      <div className="rdw-inline-wrapper">
        <div
          className={`rdw-option-wrapper${editor.isActive('bulletList') ? ' rdw-option-active' : ''}`}
          onMouseDown={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleBulletList().run()
          }}
          title="Bullet List"
        >
          &#8226;&#8212;
        </div>
        <div
          className={`rdw-option-wrapper${editor.isActive('orderedList') ? ' rdw-option-active' : ''}`}
          onMouseDown={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleOrderedList().run()
          }}
          title="Ordered List"
        >
          1.
        </div>
      </div>
      <ColorPicker editor={editor} />
      <LinkButton editor={editor} />
      <PhoneButton editor={editor} disableHandleChange={disableHandleChange} />
    </div>
  )
}

function IARRte(props) {
  const { disableHandleChange } = props
  const isProgrammaticUpdate = useRef(false)
  const onChangeRef = useRef(props.onChange)
  onChangeRef.current = props.onChange

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({ link: false, underline: false }),
        Underline,
        Link.configure({ openOnClick: false }),
        TextStyle,
        FontSize,
        Color,
        Highlight.configure({ multicolor: true })
      ],
      content: props.content || '',
      editable: !disableHandleChange,
      onUpdate: ({ editor }) => {
        if (!isProgrammaticUpdate.current && onChangeRef.current) {
          onChangeRef.current(editor.getHTML())
        }
      },
      editorProps: {
        transformPastedHTML(html) {
          return sanitizeHtml(html)
            .replace(/(<\/?)img((?:\s+.*?)?>)/g, '')
            .replace(/(<\/?)figure((?:\s+.*?)?>)/g, '')
        }
      }
    }
  )

  useEffect(() => {
    if (!editor) return
    if (props.content === '' || props.reset) {
      if (editor.getHTML() !== '<p></p>') {
        isProgrammaticUpdate.current = true
        editor.commands.clearContent()
        queueMicrotask(() => {
          isProgrammaticUpdate.current = false
        })
      }
      return
    }
    if (props.discarded || (props.content && props.content !== '<p><br></p>')) {
      if (editor.getHTML() !== props.content) {
        isProgrammaticUpdate.current = true
        editor.commands.setContent(props.content || '')
        queueMicrotask(() => {
          isProgrammaticUpdate.current = false
        })
      }
    }
    // Do not add props.content to the array of dependencies below
  }, [editor, props.content, props.contentState, props.discarded, props.reset])

  useEffect(() => {
    if (!editor) return
    editor.setEditable(!disableHandleChange)
  }, [editor, disableHandleChange])

  return (
    <div className="container-root">
      <div className="rich-editor">
        <Toolbar editor={editor} disableHandleChange={disableHandleChange} />
        <EditorContent
          editor={editor}
          className={`content-editor${disableHandleChange ? ' blockStyleForDisabled' : ''}`}
        />
      </div>
    </div>
  )
}

IARRte.propTypes = {
  content: PropTypes.string,
  initialValueLoaded: PropTypes.bool
}

export { IARRte }
